import { EqlFieldsAutoComplete } from '@modules/common/components/EqlFieldsAutoComplete';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import { FieldIcon } from '@modules/common/components/FieldIcon';
import { useFetchEQLFields } from '@modules/common/hooks';
import { SelectButton } from '@modules/queryBuilder/components/SelectButton';
import { MULTI_VALUE_OPERATORS } from '@modules/queryBuilder/constants';
import {
  getFallbackDisplayNameForEqlField,
  getFieldOperators,
  removeQueryFilterNode,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { useFetchCustomFilter } from '@modules/queryBuilder/hooks';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { EqlField, EqlFieldValueType } from 'generated/graphql';
import { SyntheticEvent } from 'react';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';

interface QueryFilterFieldSelectButtonProps {
  filter: NormalisedFilter;
  onChange?: (field: string | null) => void;
}

export function QueryFilterFieldSelectButton({
  filter,
  onChange,
}: QueryFilterFieldSelectButtonProps) {
  const { fields } = useFetchEQLFields({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchAndApplyFilters] = useFetchCustomFilter();
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const errorFilterID = useQueryBuilderStore(
    ({ metadata }) => metadata.error?.filterID,
  );
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );

  const getLabel = () => {
    if (filter.field === 'search') {
      return 'Text search';
    }
    const eqlField = fields.find(({ field }) => field === filter.field);
    return eqlField
      ? getFormattedDisplayNameForEqlField(eqlField)
      : getFallbackDisplayNameForEqlField({
          field: filter.field,
          shouldShowSource: true,
        });
  };

  const onFieldChange = async (_: SyntheticEvent, eqlField: EqlField) => {
    const newField = eqlField.field;

    if (filter.field === newField) {
      return;
    }

    if (eqlField.valueType === EqlFieldValueType.None) {
      const analysisID = eqlField.detail?.reference?.referenceID;
      await fetchAndApplyFilters(analysisID);
      return;
    }

    let updatedNodes;

    if (newField) {
      const operator = getFieldOperators({ field: newField })[0];

      updatedNodes = upsertQueryFilterNode({
        node: {
          ...filter,
          field: newField,
          // ##TODO, get this value from field (Use getFilterValueType function)
          value: {
            type: MULTI_VALUE_OPERATORS.includes(operator)
              ? 'string[]'
              : 'string',
            values: [],
          },
          operator,
        },
        nodesByID: nodes,
        parentID: filter.parent,
      });
    } else {
      updatedNodes = removeQueryFilterNode({
        node: { ...filter },
        nodes,
        parentID: filter.parent,
      });
    }

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        eqlFieldByFieldID: {
          ...eqlFieldByFieldID,
          [eqlField.field]: {
            ...eqlFieldByFieldID[eqlField.field],
            field: eqlField,
          },
        },
      },
    });

    onChange?.(newField);
  };

  const shouldShowError = errorFilterID === filter.id;

  return (
    <SelectButton
      className="qb-filter-field-select-button"
      shouldShowTooltip={false}
      label={getLabel()}
      startIcon={
        <FieldIcon
          field={filter.field}
          iconURL={eqlFieldByFieldID[filter.field]?.field?.iconURL}
          fontSize="small"
          valueType={eqlFieldByFieldID[filter.field]?.field?.valueType}
          objectType={eqlFieldByFieldID[filter.field]?.field?.objectType}
        />
      }
      componentsProps={{
        popper: { sx: { width: 400 } },
        button: {
          color: shouldShowError ? 'error' : undefined,
          sx: {
            maxWidth: 250,
          },
        },
      }}
      renderDropDownComponent={({ open, onClose }) => (
        <EqlFieldsAutoComplete
          id={`qb-filter-field-dropdown-${filter.id}`}
          open={open}
          onChange={onFieldChange}
          onClose={onClose}
          shouldExcludeCustomFilters
        />
      )}
    />
  );
}
